import {
  Box,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  Tooltip,
} from "@mui/material";

import InfoIcon from "../../../../common/components/icons/InfoIcon";

export type SortableColumn =
  | "skuNumber"
  | "serialNumber"
  | "configurationName"
  | "aggregateStatus";

export type SortDirection = "asc" | "desc";

interface IGroupedDevicesTableHeadRowProps {
  orderBy: SortableColumn;
  order: SortDirection;
  onSort: (column: SortableColumn) => void;
}

const GroupedDevicesTableHeadRow = ({
  orderBy,
  order,
  onSort,
}: IGroupedDevicesTableHeadRowProps): JSX.Element => {
  const createSortHandler = (column: SortableColumn) => () => {
    onSort(column);
  };

  return (
    <TableHead
      sx={{
        "& .MuiTableCell-head": {
          fontWeight: 600,
          fontSize: "12px",
          textTransform: "uppercase",
          backgroundColor: (theme: Theme) =>
            theme.palette.otherBackdropOverlay.main,
          color: (theme: Theme) => theme.palette.text.secondary,
          borderBottom: (theme: Theme) =>
            `1px solid ${theme.palette.otherOutlineBorder.main}`,
        },
        "& tr:first-of-type th:first-of-type": {
          borderTopLeftRadius: "12px",
        },
        "& tr:first-of-type th:last-of-type": {
          borderTopRightRadius: "12px",
        },
      }}
    >
      <TableRow>
        {/* Expand/Collapse icon column */}
        <TableCell sx={{ width: 48 }} />
        {/* SKU column */}
        <TableCell
          sx={{ width: 150 }}
          sortDirection={orderBy === "skuNumber" ? order : false}
        >
          <TableSortLabel
            active={orderBy === "skuNumber"}
            direction={orderBy === "skuNumber" ? order : "asc"}
            onClick={createSortHandler("skuNumber")}
          >
            SKU
          </TableSortLabel>
        </TableCell>
        {/* SKU Serial Number column */}
        <TableCell
          sx={{ width: 300 }}
          sortDirection={orderBy === "serialNumber" ? order : false}
        >
          <TableSortLabel
            active={orderBy === "serialNumber"}
            direction={orderBy === "serialNumber" ? order : "asc"}
            onClick={createSortHandler("serialNumber")}
          >
            SKU SN
          </TableSortLabel>
        </TableCell>
        {/* Configuration column */}
        <TableCell
          sx={{ width: 362 }}
          sortDirection={orderBy === "configurationName" ? order : false}
        >
          <TableSortLabel
            active={orderBy === "configurationName"}
            direction={orderBy === "configurationName" ? order : "asc"}
            onClick={createSortHandler("configurationName")}
          >
            CONFIGURATION
          </TableSortLabel>
        </TableCell>
        {/* Spacer - stretchable, pushes Status/Actions to the right */}
        <TableCell />
        {/* Status column */}
        <TableCell
          sx={{ width: 115 }}
          sortDirection={orderBy === "aggregateStatus" ? order : false}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <TableSortLabel
              active={orderBy === "aggregateStatus"}
              direction={orderBy === "aggregateStatus" ? order : "asc"}
              onClick={createSortHandler("aggregateStatus")}
            >
              STATUS
            </TableSortLabel>
            <Tooltip
              sx={{
                display: "flex",
                alignItems: "center",
              }}
              title="Online = all components online, Degraded = some offline, Offline = all offline"
            >
              <Box>
                <InfoIcon />
              </Box>
            </Tooltip>
          </Box>
        </TableCell>
        {/* Actions column */}
        <TableCell sx={{ width: 100 }} />
      </TableRow>
    </TableHead>
  );
};

export default GroupedDevicesTableHeadRow;
